// src/components/doctor/GlanceStrip.jsx
//
// Tier 1 — four tiles that are always in view: why the patient came, today's
// vitals (flags as arrows + words), allergies (text / none recorded / not
// available) and what needs attention (opens the context drawer).

import React from 'react';
import { vitalChips, vitalsHeading, unplacedFlags, localTime } from '../../utils/consultVitals';

function VitalsTile({ vitals, appointmentDate, onRecord }) {
  const heading = vitalsHeading(vitals, appointmentDate);
  const chips = vitalChips(vitals);
  return (
    <div className="consult-tile consult-tile--vitals" data-testid="tile-vitals">
      <div className="consult-tile__k">{heading.label}</div>
      {vitals === undefined ? <div className="skeleton skeleton--row" />
        : chips.length === 0 ? <div className="consult-tile__m">No vitals recorded yet</div>
          : (
            <div className="consult-chips">
              {chips.map(c => (
                <span key={c.key} className={`consult-chip${c.flagged ? ' consult-chip--flag' : ''}`} title={c.flagText || undefined}>
                  {c.text}
                  {c.flagged && <><span aria-hidden="true">{c.arrow}</span><span className="sr-only"> ({c.flagText})</span></>}
                </span>
              ))}
              {unplacedFlags(vitals).map(t => <span key={t} className="consult-chip consult-chip--flag">{t}</span>)}
            </div>
          )}
      {onRecord && vitals !== undefined && (
        <button type="button" className="consult-link" onClick={onRecord} style={{ marginTop: 6 }}>
          {chips.length === 0 ? 'Record vitals' : 'Record new reading'}
        </button>
      )}
    </div>
  );
}

export default function GlanceStrip({
  appointment, visit, vitals, allergies, attention, contextStatus, onOpenAttention, onRecordVitals,
}) {
  const reason = visit?.chiefComplaint || appointment.reason || 'Not recorded';
  const heading = vitalsHeading(vitals, appointment.appointmentDate);
  const intakeBy = vitals && heading.kind === 'today' && vitals.recordedByName
    ? `Intake by ${vitals.recordedByName}${localTime(vitals.measuredAt) ? ` · ${localTime(vitals.measuredAt)}` : ''}` : '';

  const allergyTone = allergies === null || (allergies && allergies.length) ? ' consult-tile--warn' : '';
  const allergyText = allergies === undefined ? 'Checking…'
    : allergies === null ? 'Not available — check the patient record before prescribing'
      : (allergies || 'No allergies recorded');

  const n = attention?.urgentCount || 0;
  const attnValue = contextStatus === 'loading' ? 'Checking…'
    : contextStatus === 'error' ? 'Couldn’t load' : n > 0 ? `${n} urgent open item${n === 1 ? '' : 's'}` : 'Nothing urgent';
  const risk = attention?.riskScore != null
    ? `Report risk ${attention.riskScore}${attention.riskLevel ? ` ${attention.riskLevel}` : ''}` : 'No triage report yet';

  return (
    <section className="consult-glance" aria-label="Patient at a glance">
      <div className="consult-tile" data-testid="tile-reason">
        <div className="consult-tile__k">Reason for visit</div>
        <div className="consult-tile__v">{reason}</div>
        {intakeBy && <div className="consult-tile__m">{intakeBy}</div>}
      </div>
      <div className={`consult-tile consult-tile--allergy${allergyTone}`} data-testid="tile-allergies" role="note">
        <div className="consult-tile__k"><span aria-hidden="true">⚠ </span>Allergies</div>
        <div className="consult-tile__v">{allergyText}</div>
      </div>
      <VitalsTile vitals={vitals} appointmentDate={appointment.appointmentDate} onRecord={onRecordVitals} />
      <button type="button" className={`consult-tile consult-tile--attn${n > 0 ? ' consult-tile--bad' : ''}`} data-testid="tile-attention" onClick={onOpenAttention}
        aria-label={`Needs attention: ${attnValue}. ${risk}. Open details`}>
        <div className="consult-tile__k">Needs attention</div>
        <div className="consult-tile__v">{attnValue}</div>
        <div className="consult-tile__m">{risk} · <span className="consult-link" aria-hidden="true">Review →</span></div>
      </button>
    </section>
  );
}
